import React, { forwardRef, useCallback, useRef } from 'react';

// DEPENDENCIES
import InfiniteLoader from 'react-window-infinite-loader';
import { VariableSizeList } from 'react-window';
import InfiniteScroll from 'react-infinite-scroll-component';

// HELPERS
import { useListData } from 'helpers/getListData';

// STYLES
import styles from './virtualizedLoader.module.scss';

// MAIN COMPONENT
export const virtualizedLoader = Component => {
  const WrappedComponent = ({
    children, //
    forwardedRef,
    height,
    numLoaded: numLoadedProp,
    rowHeight,
    setRef,
    virtualization = true,
    width,
    ...props
  }) => {
    // REFS
    const listRef = useRef({});
    const rowHeights = useRef({});

    // LIST CONTEXT / PROPS
    const {
      result: rows = [],
      data: { start_row, startRow = start_row } = {},
      loading,
      updateContext
    } = { ...(useListData() || {}), ...props };

    const numLoaded = numLoadedProp || rows?.length;

    // CALLBACKS
    const isItemLoaded = useCallback(
      index => {
        const hasNextPage = !!startRow;

        return !hasNextPage || index < numLoaded;
      },
      [numLoaded, startRow]
    );

    const loadMoreItems = useCallback(() => {
      if (!startRow || loading) return;

      updateContext({ type: 'paginate' });
    }, [loading, startRow, updateContext]);

    const getRowHeight = useCallback(
      index => {
        return rowHeights.current[index] || rowHeight;
      },
      [rowHeight]
    );

    const setRowHeight = useCallback((index, size, listRef) => {
      listRef.current.resetAfterIndex(0);
      rowHeights.current = { ...rowHeights.current, [index]: size };
    }, []);

    // height is undefined if autoSizeHeight prop is false
    // Cannot use virtualization without a specified height
    return height && virtualization ? (
      <InfiniteLoader isItemLoaded={isItemLoaded} itemCount={Infinity} loadMoreItems={loadMoreItems}>
        {({ onItemsRendered, ref: loaderRef }) => (
          <VariableSizeList
            className={styles.list}
            height={height}
            itemCount={numLoaded}
            itemSize={getRowHeight}
            onItemsRendered={onItemsRendered}
            outerRef={setRef ? node => setRef(node) : forwardedRef}
            ref={list => {
              loaderRef(list);
              listRef.current = list;
            }}
            width={width + parseInt(styles['padding-x'])}
          >
            {({ index, style }) => (
              <Component
                index={index}
                ref={listRef}
                rows={rows}
                setRowHeight={setRowHeight}
                rowHeight={rowHeight}
                style={style}
                width={width}
                {...props}
              />
            )}
          </VariableSizeList>
        )}
      </InfiniteLoader>
    ) : (
      <InfiniteScroll
        dataLength={numLoaded}
        next={loadMoreItems}
        hasMore={startRow}
        height={height}
        ref={setRef ? node => setRef(node) : forwardedRef}
        style={{ width: width + parseInt(styles['padding-x']) }}
      >
        {rows.map((_, index) => (
          <Component
            key={index}
            index={index}
            ref={listRef}
            rows={rows}
            rowHeight={rowHeight}
            width={width}
            {...props}
          />
        ))}
      </InfiniteScroll>
    );
  };

  return forwardRef((props, ref) => <WrappedComponent {...props} forwardedRef={ref} />);
};
